import { Children, isValidElement, type ReactNode } from 'react';
import { cn } from './cn';

interface Item { label?: ReactNode; children?: ReactNode; span?: number; }

function DescriptionsItem(_props: Item) { return null; }

const COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

/** Ant Design `Descriptions` replacement — a clean label/value grid (children or items API). */
export const Descriptions = Object.assign(
  function Descriptions({
    title, extra, bordered, column = 3, size, items, className, children,
    style, colon,
  }: {
    title?: ReactNode; extra?: ReactNode; bordered?: boolean;
    column?: number; size?: 'small' | 'middle' | 'default';
    items?: Item[]; className?: string; children?: ReactNode;
    style?: import('react').CSSProperties; colon?: boolean;
  }) {
    void colon;
    const rows: Item[] = items ?? Children.toArray(children).filter(isValidElement).map((c) => {
      const p = (c as { props: Item }).props;
      return { label: p.label, children: p.children, span: p.span };
    });
    const cols = Math.min(Math.max(typeof column === 'number' ? column : 3, 1), 4);
    const pad = size === 'small' ? 'gap-y-3' : 'gap-y-4';
    return (
      <div className={className} style={style}>
        {(title != null || extra != null) && (
          <div className="mb-3 flex items-center justify-between gap-3">
            {title != null && <div className="text-base font-semibold text-content">{title}</div>}
            {extra != null && <div className="text-sm text-muted">{extra}</div>}
          </div>
        )}
        <div className={cn('grid gap-x-6', pad, COLS[cols], bordered && 'rounded-card border border-line bg-surface p-4')}>
          {rows.map((r, i) => (
            <div key={i} style={r.span ? { gridColumn: `span ${Math.min(r.span, cols)} / span ${Math.min(r.span, cols)}` } : undefined}
              className={bordered ? 'border-b border-line/60 pb-3 last:border-0' : undefined}>
              <div className="text-[11px] font-medium uppercase tracking-wide text-subtle">{r.label}</div>
              <div className="mt-0.5 break-words text-sm text-content">{r.children}</div>
            </div>
          ))}
        </div>
      </div>
    );
  },
  { Item: DescriptionsItem },
);
